// EVM wallets over EIP-1193, discovered with EIP-6963 (falls back to `window.ethereum`). The wallet
// signs and broadcasts; the demo only reads through it (allowance, balance, receipts).

import { decodeEventLog, decodeFunctionData, encodeFunctionData, erc20Abi, getAddress, parseAbi, type Hex } from "viem";
import type { Approval, PermitOffer, PostedPermit } from "./api";

export type Eip1193 = {
  request(args: { method: string; params?: unknown[] }): Promise<any>;
  on?(e: string, f: (...a: any[]) => void): void;
  removeListener?(e: string, f: (...a: any[]) => void): void;
};
export type EvmWalletInfo = { uuid: string; name: string; icon: string; rdns?: string; provider: Eip1193 };

/** Every injected wallet that announces itself (EIP-6963), plus `window.ethereum` if none do. */
export function discoverEvmWallets(onChange: (ws: EvmWalletInfo[]) => void): () => void {
  const found = new Map<string, EvmWalletInfo>();
  const emit = () => onChange([...found.values()]);
  const onAnnounce = (e: Event) => {
    const d = (e as CustomEvent).detail as { info: { uuid: string; name: string; icon: string; rdns?: string }; provider: Eip1193 };
    if (!d?.info?.uuid) return;
    found.set(d.info.uuid, { ...d.info, provider: d.provider });
    emit();
  };
  window.addEventListener("eip6963:announceProvider", onAnnounce);
  window.dispatchEvent(new Event("eip6963:requestProvider"));
  const fallback = setTimeout(() => {
    const eth = (window as any).ethereum as Eip1193 | undefined;
    if (!found.size && eth) {
      found.set("injected", { uuid: "injected", name: "Browser wallet", icon: "", provider: eth });
      emit();
    }
  }, 400);
  return () => {
    window.removeEventListener("eip6963:announceProvider", onAnnounce);
    clearTimeout(fallback);
  };
}

export async function connectEvm(p: Eip1193): Promise<`0x${string}`> {
  const [a] = (await p.request({ method: "eth_requestAccounts" })) as string[];
  if (!a) throw new Error("the wallet returned no account");
  return getAddress(a);
}

/** Follow the wallet's selected account: `onChange(address)` on a switch, `onChange(null)` when it
 *  exposes none (locked or disconnected from the wallet side). Returns the unsubscribe. */
export function watchEvmAccount(p: Eip1193, onChange: (address: `0x${string}` | null) => void): () => void {
  const on = (accs: string[]) => onChange(accs?.[0] ? getAddress(accs[0]) : null);
  p.on?.("accountsChanged", on);
  return () => p.removeListener?.("accountsChanged", on);
}

/** Follow the wallet's network: `onChange(chainId)` now and on every switch. Returns the unsubscribe. */
export function watchEvmChain(p: Eip1193, onChange: (chainId: number | null) => void): () => void {
  let live = true;
  const on = (id: string) => live && onChange(id ? Number(id) : null);
  p.request({ method: "eth_chainId" }).then(on, () => on(""));
  p.on?.("chainChanged", on);
  return () => {
    live = false;
    p.removeListener?.("chainChanged", on);
  };
}

/** Switch the wallet to `chainId`. A wallet that does not know the chain says so (4902): with
 *  `add` it is offered the chain (`wallet_addEthereumChain`, which also switches), else the user
 *  is told to add it. */
export async function ensureChain(
  p: Eip1193,
  chainId: number,
  label: string,
  add?: { rpcUrls: string[]; nativeCurrency: { name: string; symbol: string; decimals: number }; blockExplorerUrls: string[] },
): Promise<void> {
  const current = Number(await p.request({ method: "eth_chainId" }));
  if (current === chainId) return;
  const hex = `0x${chainId.toString(16)}`;
  try {
    await p.request({ method: "wallet_switchEthereumChain", params: [{ chainId: hex }] });
  } catch (e: any) {
    if (e?.code !== 4902) throw walletError(e, `switch to ${label}`);
    if (!add) throw new Error(`your wallet does not have ${label} (chain ${chainId}); add it and retry`);
    try {
      await p.request({ method: "wallet_addEthereumChain", params: [{ chainId: hex, chainName: label, ...add }] });
    } catch (e2: any) {
      throw walletError(e2, `add ${label} to your wallet`);
    }
  }
}

/** Human message for a wallet error (4001 = the user rejected). */
export function walletError(e: any, what: string): Error {
  if (e?.code === 4001 || /user (rejected|denied)/i.test(String(e?.message))) return new Error(`you rejected the ${what} in your wallet`);
  return new Error(`${what}: ${e?.shortMessage ?? e?.message ?? String(e)}`);
}

const call = (p: Eip1193, to: string, data: Hex) => p.request({ method: "eth_call", params: [{ to, data }, "latest"] }) as Promise<Hex>;

export async function ethBalance(p: Eip1193, owner: string): Promise<bigint> {
  return BigInt((await p.request({ method: "eth_getBalance", params: [owner, "latest"] })) as Hex);
}
export async function erc20Balance(p: Eip1193, token: string, owner: string): Promise<bigint> {
  return BigInt(await call(p, token, encodeFunctionData({ abi: erc20Abi, functionName: "balanceOf", args: [owner as Hex] })));
}
export async function erc20Allowance(p: Eip1193, token: string, owner: string, spender: string): Promise<bigint> {
  return BigInt(await call(p, token, encodeFunctionData({ abi: erc20Abi, functionName: "allowance", args: [owner as Hex, spender as Hex] })));
}
export const approveData = (spender: string, amount: bigint) => encodeFunctionData({ abi: erc20Abi, functionName: "approve", args: [spender as Hex, amount] });

export async function sendTx(p: Eip1193, from: string, tx: { to: string; data: Hex; value?: bigint }, what: string): Promise<Hex> {
  try {
    return (await p.request({ method: "eth_sendTransaction", params: [{ from, to: tx.to, data: tx.data, value: `0x${(tx.value ?? 0n).toString(16)}` }] })) as Hex;
  } catch (e) {
    throw walletError(e, what);
  }
}

/** Dry-run through the wallet's RPC: a revert surfaces before the user pays gas. */
export async function simulate(p: Eip1193, from: string, tx: { to: string; data: Hex; value?: bigint }): Promise<void> {
  await p.request({ method: "eth_call", params: [{ from, to: tx.to, data: tx.data, value: `0x${(tx.value ?? 0n).toString(16)}` }, "latest"] });
}

export type Receipt = { status: Hex; blockNumber: Hex; logs: { address: string; data: Hex; topics: Hex[] }[] };

export async function waitReceipt(p: Eip1193, hash: Hex, timeoutMs = 180_000): Promise<Receipt> {
  const until = Date.now() + timeoutMs;
  while (Date.now() < until) {
    const r = (await p.request({ method: "eth_getTransactionReceipt", params: [hash] }).catch(() => null)) as Receipt | null;
    if (r) {
      if (r.status !== "0x1") throw new Error(`transaction ${hash} reverted`);
      return r;
    }
    await new Promise((res) => setTimeout(res, 1500));
  }
  throw new Error(`no receipt for ${hash} after ${timeoutMs / 1000} s; check the explorer`);
}

export const GUM_EVENTS = parseAbi([
  "event Opened(address indexed user, uint64 indexed escrowId, bytes32 indexed intentHash, uint64 intentNonce, uint64 dstChainId, bytes rawMessage, address builder, uint256 builderFee, uint256 protocolFee)",
  // the hosted swap router's own event, for a receipt whose `to` is the router
  "event SwapOpened(address indexed user, bytes32 indexed intentHash, address tokenIn, uint256 amountIn, uint256 stableOut)",
]);

/** The `intent_hash` from an open's receipt: the proxy's `Opened`, or the swap router's
 *  `SwapOpened` when `emitter` (the transaction's `to`) is the router. Only `emitter`'s logs
 *  count: another contract in the same transaction could emit a look-alike. */
export function openedIntentHash(receipt: Pick<Receipt, "logs">, emitter: string): Hex | null {
  for (const l of receipt.logs) {
    if (getAddress(l.address) !== getAddress(emitter)) continue;
    try {
      const d = decodeEventLog({ abi: GUM_EVENTS, data: l.data, topics: l.topics as [Hex, ...Hex[]] });
      if (d.eventName === "Opened" || d.eventName === "SwapOpened") return d.args.intentHash;
    } catch {
      // another event of the emitter
    }
  }
  return null;
}

// ---- hosted swap approvals (builders-ui.md §4) -----------------------------------------------

/** How a hosted EVM swap gets the router its tokens, best first. */
export type EvmPath = "none" | "batch" | "permit" | "approve";

/** `none`: ETH or an allowance that covers it. `batch`: the wallet sends approve + open atomically
 *  (EIP-5792). `permit`: a gasless signature, then one transaction. `approve`: two transactions. */
export function evmPath(b: { approval?: Approval | null; permit?: PermitOffer | null }, atomicBatch: boolean): EvmPath {
  if (!b.approval) return "none";
  if (atomicBatch) return "batch";
  if (b.permit) return "permit";
  return "approve";
}

/** EIP-5792 `wallet_getCapabilities` → whether `chainId` takes an atomic batch as the account is.
 *  Covers the 2.0 shape (`atomic.status`) and the earlier `atomicBatch.supported`. `"ready"` is NOT
 *  enough: it means the wallet would first upgrade the account (EIP-7702 delegation to the wallet's
 *  smart account, a lasting change the wallet warns about), so a swap falls back to a permit or an
 *  approval instead of asking for that. */
export function supportsAtomic(caps: any, chainId: number): boolean {
  const c = caps?.[`0x${chainId.toString(16)}`] ?? caps?.[String(chainId)] ?? caps?.["0x0"];
  if (!c) return false;
  return c.atomic?.status === "supported" || c.atomicBatch?.supported === true;
}

export async function walletAtomic(p: Eip1193, address: string, chainId: number): Promise<boolean> {
  try {
    return supportsAtomic(await p.request({ method: "wallet_getCapabilities", params: [address, [`0x${chainId.toString(16)}`]] }), chainId);
  } catch {
    return false; // no EIP-5792
  }
}

export type CallsStatus = { state: "pending" | "confirmed" | "failed"; receipts: Receipt[]; txHash?: Hex };

/** `wallet_getCallsStatus` → one shape: 2.0 numeric codes (1xx pending, 200 confirmed, ≥ 400
 *  failed) or the earlier "PENDING" / "CONFIRMED" strings. */
export function parseCallsStatus(r: any): CallsStatus {
  const receipts: Receipt[] = r?.receipts ?? [];
  const txHash = (receipts.at(-1) as any)?.transactionHash as Hex | undefined;
  const s = r?.status;
  let state: CallsStatus["state"];
  if (typeof s === "number") state = s >= 400 ? "failed" : s >= 200 ? "confirmed" : "pending";
  else state = s === "CONFIRMED" ? "confirmed" : s === "FAILED" ? "failed" : "pending";
  // a batch that landed but reverted inside is a failure, whatever the batch status says
  if (state === "confirmed" && receipts.some((x) => x.status !== "0x1")) state = "failed";
  return { state, receipts, txHash };
}

/** The wallet took the batch: whatever happens next, it may still execute, so the caller must
 *  never fall back to another approval or open (it would be a second swap). */
export class BatchSubmitted extends Error {
  constructor(message: string, readonly batchId: string, readonly txHash?: Hex) {
    super(message);
  }
}

/** Whether a failed batch may fall back to the permit / approval path: only a wallet that refused
 *  the batch before taking it (a capability it announced but does not honour), and not the user's
 *  own rejection. */
export function batchFallbackAllowed(e: unknown): boolean {
  if (e instanceof BatchSubmitted) return false;
  return !/rejected/.test(String((e as any)?.message ?? e));
}

/** Send `calls` as one atomic EIP-5792 batch and wait for it; returns its receipts. Throws
 *  `BatchSubmitted` for anything after the wallet took it (failed, or not confirmed in time). */
export async function sendBatch(p: Eip1193, from: string, chainId: number, calls: { to: string; data: Hex; value?: bigint }[], timeoutMs = 180_000, pollMs = 1500): Promise<CallsStatus> {
  let id: any;
  try {
    id = await p.request({
      method: "wallet_sendCalls",
      params: [{ version: "2.0.0", chainId: `0x${chainId.toString(16)}`, from, atomicRequired: true, calls: calls.map((c) => ({ to: c.to, data: c.data, value: `0x${(c.value ?? 0n).toString(16)}` })) }],
    });
  } catch (e) {
    throw walletError(e, "approve + open");
  }
  const batchId = typeof id === "string" ? id : id?.id;
  const until = Date.now() + timeoutMs;
  while (Date.now() < until) {
    const st = parseCallsStatus(await p.request({ method: "wallet_getCallsStatus", params: [batchId] }).catch(() => null));
    if (st.state === "failed") throw new BatchSubmitted(`the approve + open batch failed${st.txHash ? ` (${st.txHash})` : ""}; nothing was opened`, batchId, st.txHash);
    if (st.state === "confirmed") return st;
    await new Promise((res) => setTimeout(res, pollMs));
  }
  throw new BatchSubmitted(`the approve + open batch (${batchId}) is still pending after ${timeoutMs / 1000} s; check your wallet before trying again, it may still open`, batchId);
}

/** The intent hash from a batch's receipts (the open is the last call). */
export function batchIntentHash(receipts: Pick<Receipt, "logs">[], router: string): Hex | null {
  for (const r of [...receipts].reverse()) {
    const h = openedIntentHash(r, router);
    if (h) return h;
  }
  return null;
}

export async function signTypedData(p: Eip1193, from: string, typedData: any): Promise<Hex> {
  try {
    return (await p.request({ method: "eth_signTypedData_v4", params: [from, JSON.stringify(typedData)] })) as Hex;
  } catch (e) {
    throw walletError(e, "permit signature");
  }
}

/** The `/build` `permit` field for a signed offer. */
export function postedPermit(offer: PermitOffer, signature: string): PostedPermit {
  if (offer.kind === "permit2") {
    if (!offer.nonce) throw new Error("the api's Permit2 offer has no nonce");
    return { kind: "permit2", deadline: offer.deadline, nonce: offer.nonce, signature };
  }
  return { kind: "eip2612", deadline: offer.deadline, signature };
}

/** The api's swap router (contracts/evm/src/GumIntentSwapRouter.sol): its three entry points. */
export const ROUTER_ABI = parseAbi([
  "struct Swap { address tokenIn; uint256 amountIn; address target; address spender; bytes data; }",
  "struct OpenArgs { uint64 dstChainId; bytes32 dstEndpoint; address inputToken; uint256 inputAmount; uint256 minGross; uint256 maxGross; uint256 rateNum; uint256 rateDen; uint16 slippageBps; bytes dstToken; bytes32 dstRecipient; uint64 validitySeconds; address builder; uint16 builderFeeBps; uint16 protocolFeeBps; uint8 delivery; }",
  "struct Permit2612 { uint256 deadline; uint8 v; bytes32 r; bytes32 s; }",
  "struct TokenPermissions { address token; uint256 amount; }",
  "struct PermitTransferFrom { TokenPermissions permitted; uint256 nonce; uint256 deadline; }",
  "function swapAndOpen(Swap s, OpenArgs a, bytes apiSignature) payable returns (uint64, bytes32)",
  "function swapAndOpenWithPermit(Swap s, Permit2612 p, OpenArgs a, bytes apiSignature) returns (uint64, bytes32)",
  "function swapAndOpenWithPermit2(Swap s, PermitTransferFrom p, bytes permitSignature, OpenArgs a, bytes apiSignature) returns (uint64, bytes32)",
]);

/** The permit variant of a built `swapAndOpen`, encoded here: the swap, the open's terms and the
 *  api's signature are the built call's own, so the user signs a permit for exactly the quote
 *  they confirmed and nothing is re-built (a second `/build` would re-price the swap). */
export function permitCalldata(plain: Hex, permit: PostedPermit): Hex {
  const call = decodeFunctionData({ abi: ROUTER_ABI, data: plain });
  if (call.functionName !== "swapAndOpen") throw new Error(`expected a swapAndOpen call, got ${call.functionName}`);
  const [s, a, apiSignature] = call.args;
  if (permit.kind === "eip2612") {
    const sig = permit.signature.replace(/^0x/, "");
    if (sig.length !== 130) throw new Error("the permit signature must be 65 bytes");
    const v = parseInt(sig.slice(128, 130), 16);
    return encodeFunctionData({
      abi: ROUTER_ABI,
      functionName: "swapAndOpenWithPermit",
      args: [s, { deadline: BigInt(permit.deadline), v: v < 27 ? v + 27 : v, r: `0x${sig.slice(0, 64)}`, s: `0x${sig.slice(64, 128)}` }, a, apiSignature],
    });
  }
  return encodeFunctionData({
    abi: ROUTER_ABI,
    functionName: "swapAndOpenWithPermit2",
    args: [s, { permitted: { token: s.tokenIn, amount: s.amountIn }, nonce: BigInt(permit.nonce), deadline: BigInt(permit.deadline) }, permit.signature as Hex, a, apiSignature],
  });
}
